/**
 * What may be done to a PHASE, and why not where it may not.
 *
 * The phase is the one level of this tree where POSITION carries identity: `PhaseGrowthPolicy`
 * walks the stored phases and the submitted ones as two sequences and judges the submission at the
 * SAME index, so an insertion, a deletion or a reorder anywhere inside the consumed prefix hands a
 * trained cycle's phase to a different row. Day and exercise `Order` may be renumbered freely
 * (`SlotToken` carries their identity); this level may not. Every rule below falls out of that.
 *
 * ── It mirrors a server rule; it does not enforce one ────────────────────────────────────────
 * Same contract as `livePlanLocks.ts`, and for the same reason — view ADR §8, "no control is
 * enabled whose action the server will refuse". Mobile's C5 defect is a phase-length stepper that
 * stayed enabled on a running plan while the backend rejected every resulting save. The refusal
 * codes each gate corresponds to are named on the members of `PhaseEditRefusal`, so a gate that
 * drifts from the backend is findable by grepping the code the trainer would meet instead.
 *
 * ── Read from `Services/PhaseGrowthPolicy.cs`, not from the ADR ──────────────────────────────
 * Three rules, and the third is the one that is easy to get wrong:
 *
 * · A phase whose FIRST cycle is consumed is frozen in identity, position and type. It may not be
 *   deleted and may not be retyped. (`plan_phase_structure_locked`)
 * · Its shortest legal length is `min(currentCycles, consumed − start + 1)` — so a phase entirely
 *   inside the prefix cannot shrink at all, and the one STRADDLING the boundary may give back every
 *   cycle above it. (`plan_phase_shrink_blocked`)
 * · Growth is refused only when the phase CLOSED strictly before the boundary — `end < consumed`.
 *   A phase ending exactly ON the last consumed cycle may still grow, and that is the phase the
 *   trainer most often wants to extend. (`plan_phase_completed_locked`)
 *
 * ── Where this is deliberately more permissive than the phone ────────────────────────────────
 * Mobile's `phaseStepperLimits` refuses growth whenever `Σ cycles >= 52`. The backend's cap is
 * GROWTH-RELATIVE (`exceedsTotalCycleCap`): a submission is refused only when it both exceeds 52 and
 * is longer than what the plan already spans, so a legacy plan stored at 55 cycles stays editable.
 * This module mirrors the backend rather than the phone, because the phone's version locks exactly
 * the plans that most need an edit. `currentTotalCycles` is the span the plan was LOADED with —
 * `live.totalCycles`, which `responseToDraft` computes once and no operation touches.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. It answers in refusal CODES; the
 * surface that shows a disabled control words them.
 */

import { phaseStartCycles } from './cycleWindow';
import { consumedCycleCount, isLivePlan } from './livePlanLocks';
import { COLLECTION_CAPS, MAX_TOTAL_CYCLES, exceedsTotalCycleCap, type CapRange } from './planCaps';
import type { PlanDraft } from './planDraft.types';

/**
 * Why a phase edit is not offered.
 *
 * Each member names the backend refusal it stands in front of, so the gate and the message the
 * trainer would otherwise have met are traceable to each other:
 *
 * · `phaseConsumed`        → `plan_phase_structure_locked`
 * · `closedPhaseCopy`      → `plan_phase_structure_locked`
 * · `shrinkBelowConsumed`  → `plan_phase_shrink_blocked`
 * · `completedPhaseGrowth` → `plan_phase_completed_locked`
 * · `totalCyclesCapped`    → `plan_total_cycles_capped`
 *
 * **`phaseConsumed` and `closedPhaseCopy` stand in front of ONE backend code and are still two
 * members**, because the sentence a trainer needs is different and the conditions are different:
 * the first refuses changing or deleting a phase the client has trained INTO (`start <= consumed`),
 * the second refuses copying a phase the client has trained THROUGH (`end < consumed`), and it does
 * so because the copy lands after the source and pushes the block they are on. One code meant one
 * sentence, which had to cover both, which made it 208 characters — and the editor prints it under
 * Duplicate and again in the panel's footer, so the same five lines of 9px text appeared twice.
 *
 * `lastPhase` and `phaseCap` have no backend code because they mirror collection bounds
 * (`PostPlanTemplateRequest.Phases`, `[MinLength(1)] [MaxLength(12)]`) that the wire states as a
 * validation error rather than a domain refusal.
 */
export type PhaseEditRefusal =
  | 'phaseConsumed'
  | 'closedPhaseCopy'
  | 'shrinkBelowConsumed'
  | 'completedPhaseGrowth'
  | 'totalCyclesCapped'
  | 'lastPhase'
  | 'phaseCap';

/** How long this phase may be made, and — when the bounds are not the plain ones — why. */
export interface PhaseCycleBounds extends CapRange {
  /** Why `min` is above 1. `null` when it is 1. */
  readonly floorReason: Extract<PhaseEditRefusal, 'shrinkBelowConsumed'> | null;
  /** Why `max` is not the plain per-phase ceiling. `null` when the phase may still grow to it. */
  readonly ceilingReason: Extract<
    PhaseEditRefusal,
    'completedPhaseGrowth' | 'totalCyclesCapped'
  > | null;
}

/** Everything a phase editor may do to ONE phase. */
export interface PhaseEditGates {
  readonly cycles: PhaseCycleBounds;
  /** `null` when the phase may be deleted. */
  readonly removal: PhaseEditRefusal | null;
  /** `null` when its Build/Deload type may be changed. */
  readonly retype: PhaseEditRefusal | null;
  /** `null` when it may be copied. A copy lands directly after its source, so the prefix decides. */
  readonly duplication: PhaseEditRefusal | null;
}

/**
 * The span the plan was LOADED with — the baseline the growth-relative cap is measured against.
 *
 * A plan that has never been stored spans nothing, so every cycle in the draft is growth. That is
 * the backend's own reading: `EnsureSubmittedWithinTotalCap` is called with `0` at a create door.
 * `createEmptyDraft` seeds `live.totalCycles` with the phase it constructs, which is a description
 * of the seed rather than of anything stored, so `id === null` is what this asks first.
 */
function loadedTotalCycles(draft: PlanDraft): number {
  return draft.id === null ? 0 : (draft.live.totalCycles ?? 0);
}

/** What the draft spans right now. Floored per phase the same way every other reader floors it. */
export function draftTotalCycles(draft: PlanDraft): number {
  return draft.phases.reduce((total, phase) => total + Math.max(1, phase.cycles), 0);
}

/**
 * How many cycles the plan may still gain before the total cap refuses the save — `0` when it is
 * already at or over it.
 *
 * Growth-relative, so a plan loaded at 55 cycles may keep all 55 and gain none, rather than being
 * frozen out of every edit by a naive `> 52`.
 */
function cycleAllowance(draft: PlanDraft): number {
  const current = draftTotalCycles(draft);
  const ceiling = Math.max(MAX_TOTAL_CYCLES, loadedTotalCycles(draft));
  return Math.max(0, ceiling - current);
}

/** The absolute cycle span of one phase, recomputed from the tree. `null` outside the array. */
function phaseSpan(
  draft: PlanDraft,
  phaseIndex: number,
): { readonly start: number; readonly end: number; readonly cycles: number } | null {
  const phase = draft.phases[phaseIndex];
  if (phase === undefined) return null;
  const start = phaseStartCycles(draft.phases)[phaseIndex] ?? 1;
  const cycles = Math.max(1, phase.cycles);
  return { start, end: start + cycles - 1, cycles };
}

/**
 * What may be done to the phase at `phaseIndex`, or `null` when there is no phase there.
 *
 * ONE entry point rather than five predicates, because a phase editor asks all of these at once and
 * every one of them is derived from the same two numbers — the phase's span and the consumed
 * boundary. Five exported predicates would each walk the tree again and would each have to restate
 * the span rule.
 */
export function phaseEditGates(draft: PlanDraft, phaseIndex: number): PhaseEditGates | null {
  const span = phaseSpan(draft, phaseIndex);
  if (span === null) return null;

  const perPhase = COLLECTION_CAPS.cyclesPerPhase;
  const consumed = isLivePlan(draft.live) ? consumedCycleCount(draft.live) : 0;
  const allowance = cycleAllowance(draft);

  // The prefix rules only bite once the client has actually trained something, and no extra guard
  // says so: `consumed` is 0 for an assignment nobody has started and a phase's first cycle is at
  // least 1, so both comparisons are already false there. That is the backend's own wording —
  // "B = 0 … leaves the whole structure free".
  const insidePrefix = span.start <= consumed;
  const closedBeforeBoundary = span.end < consumed;

  // `min(currentCycles, consumed − start + 1)` — the backend's `floor`, verbatim. A phase entirely
  // inside the prefix is pinned to its own length; the one holding the boundary gives back the rest.
  const floor = insidePrefix ? Math.min(span.cycles, consumed - span.start + 1) : perPhase.min;
  const min = Math.max(perPhase.min, floor);

  // A completed phase cannot grow at all, so its ceiling IS its current length. Otherwise the cap is
  // whichever bites first: the per-phase 52, or what the plan-wide allowance leaves.
  const ceiling = closedBeforeBoundary
    ? span.cycles
    : Math.min(perPhase.max, span.cycles + allowance);
  // `min` wins a collision: a consumed phase whose floor sits above a cap-squeezed ceiling must
  // still report a legal value, and shrinking it is what the trainer is being refused, not offered.
  const max = Math.max(min, ceiling);

  return {
    cycles: {
      min,
      max,
      floorReason: min > perPhase.min ? 'shrinkBelowConsumed' : null,
      ceilingReason: closedBeforeBoundary
        ? 'completedPhaseGrowth'
        : max < perPhase.max && allowance === 0
          ? 'totalCyclesCapped'
          : null,
    },
    // A plan must keep one phase — `PostPlanTemplateRequest.Phases` is `[MinLength(1)]`, and a
    // phase-less plan is a tree the serialiser cannot emit. Asked BEFORE the lock, because "this is
    // the only phase" is the more useful sentence when both are true.
    removal:
      draft.phases.length <= COLLECTION_CAPS.phasesPerPlan.min
        ? 'lastPhase'
        : insidePrefix
          ? 'phaseConsumed'
          : null,
    retype: insidePrefix ? 'phaseConsumed' : null,
    duplication: duplicationRefusal(draft, span, consumed),
  };
}

/**
 * Why this phase may not be copied.
 *
 * The copy lands DIRECTLY AFTER its source (mobile's `duplicatePhase`, and the placement a trainer
 * expects from "duplicate this block"), so the question is whether that insertion disturbs the
 * consumed prefix. It does not exactly when the source's own span reaches the boundary or past it:
 * every phase at or before the source keeps its position, and everything after the prefix is free.
 * A copy of an already-finished phase would slide the phase the client is training now one place
 * down, which is the substitution `plan_phase_structure_locked` exists to catch.
 */
function duplicationRefusal(
  draft: PlanDraft,
  span: { readonly end: number; readonly cycles: number },
  consumed: number,
): PhaseEditRefusal | null {
  // Its OWN code, not the retype/delete one: the condition is different (`end < consumed` rather
  // than `start <= consumed`) and so is the sentence a trainer needs. See `PhaseEditRefusal`.
  if (span.end < consumed) return 'closedPhaseCopy';
  if (draft.phases.length >= COLLECTION_CAPS.phasesPerPlan.max) return 'phaseCap';
  // The copy carries the source's own length, so that is what it spends against the cap.
  return exceedsTotalCycleCap(draftTotalCycles(draft) + span.cycles, loadedTotalCycles(draft))
    ? 'totalCyclesCapped'
    : null;
}

/**
 * How long a phase added by "+ Faza" runs. **One cycle, mobile's own seed** (`createPhase`), and it
 * is the length that makes the cap arithmetic honest: a longer default would have to be silently
 * shortened on a plan near the 52-cycle ceiling, and a silently shortened phase is a number the
 * trainer did not choose. The stepper is one press away.
 *
 * Deliberately NOT `draftFactory`'s `DEFAULT_PHASE_CYCLES`, which is the length of the first phase
 * of a BRAND-NEW plan — a different question with a different answer (a month of weeks, so the
 * progression matrix opens with something to project across).
 */
export const ADDED_PHASE_CYCLES = 1;

/**
 * Why a new phase may not be appended, or `null` when it may.
 *
 * A phase appended at the TAIL never disturbs the consumed prefix — the policy walks the stored
 * phases and stops at the first one starting past the boundary, and an index past the last stored
 * phase is not walked at all. So the only gates are the two collection bounds.
 */
export function addPhaseRefusal(draft: PlanDraft): PhaseEditRefusal | null {
  if (draft.phases.length >= COLLECTION_CAPS.phasesPerPlan.max) return 'phaseCap';
  return exceedsTotalCycleCap(
    draftTotalCycles(draft) + ADDED_PHASE_CYCLES,
    loadedTotalCycles(draft),
  )
    ? 'totalCyclesCapped'
    : null;
}
